import { useCallback, useMemo } from 'react';

import closeSvg from 'assets/icons/close.svg';
import FilterArea from 'components/common/FilterArea';
import { FilterDateRange } from 'components/common/FilterArea/Filters';
import FilterTextInput from 'components/common/FilterArea/Filters/FilterTextInput/FilterTextInput';
import FilterMultiStatus from 'components/common/FilterArea/Filters/FilterMultiStatus';
import { CodeNameModel } from 'components/common/types';
import { DirectSalesFilteredCampaignsStatusTableResponse } from 'components/common/types/DirectSalesCampaign.types';
import { usePageFilters } from 'customHooks/usePageFilters';
import BaseSelect from 'lib/BaseSelect';
import { findOption } from 'lib/BaseSelect/utils';
import Button, { Color, Size } from 'lib/Button';
import { DirectSalesDealManagementFilters } from './Filters.types';
import { CAMPAIGN_MEDIA_TYPES, DIRECT_SALES_CAMPAIGN_PAGE_FILTERS_KEY } from './consts';
import {
  DEFAULT_DIRECT_SALES_DEALS_CAMPAIGN_STATUS_CODES,
  DIRECT_SALES_DEALS_CAMPAIGN_STATUS_FILTER_OPTIONS,
} from './campaignStatusFilterOptions';
import AdvancedFilters from './AdvancedFilters';
import { useHasActiveFilters } from './useHasActiveFilters';

const filterItemClassName = 'w-full flex-1 min-w-[200px] basis-[200px]';
const dateRangeClassName = 'w-full max-w-full min-w-0 shrink-0 sm:w-fit';

const Filters = (): React.ReactElement => {
  const { changeFilters, resetFilters, filters } = usePageFilters<DirectSalesDealManagementFilters>(
    DIRECT_SALES_CAMPAIGN_PAGE_FILTERS_KEY,
  );

  const {
    startDate,
    endDate,
    advertiser,
    brand,
    productCategory,
    specialist,
    agency,
    updatedBy,
    createdBy,
    createdAt,
    dropDate,
    campaignName,
    campaignReference,
    campaignStatus,
    salesTeam,
    salesPerson,
    adminPerson,
    campaignMediaType,
  } = filters;

  const selectedCampaignStatusOptions = useMemo(() => {
    const statusesToDisplay = campaignStatus ?? DEFAULT_DIRECT_SALES_DEALS_CAMPAIGN_STATUS_CODES;

    return DIRECT_SALES_DEALS_CAMPAIGN_STATUS_FILTER_OPTIONS.filter((option) =>
      statusesToDisplay.includes(option.code),
    );
  }, [campaignStatus]);

  const hasActiveFilters = useHasActiveFilters(filters);

  const handleClearAllFilters = useCallback((): void => {
    resetFilters(DIRECT_SALES_CAMPAIGN_PAGE_FILTERS_KEY);
    changeFilters({ campaignStatus: [] });
  }, [changeFilters, resetFilters]);

  const handleCampaignStatusesChange = useCallback(
    (values: CodeNameModel[]) => {
      changeFilters({
        campaignStatus: values.map((value) => value.code as DirectSalesFilteredCampaignsStatusTableResponse),
      });
    },
    [changeFilters],
  );

  return (
    <FilterArea customWrapperStyles="w-full h-full flex flex-col gap-4" hideIcon>
      <div className="flex flex-wrap gap-4 items-center w-full">
        <div className={filterItemClassName}>
          <FilterMultiStatus
            statusesList={DIRECT_SALES_DEALS_CAMPAIGN_STATUS_FILTER_OPTIONS}
            selectedValues={selectedCampaignStatusOptions}
            onSelectedStatusesChange={handleCampaignStatusesChange}
          />
        </div>
        <div className={filterItemClassName}>
          <FilterTextInput
            changeFilters={changeFilters}
            keyName="campaignName"
            value={campaignName}
            name="campaign-name"
            placeholderText="Campaign name"
          />
        </div>
        <div className={filterItemClassName}>
          <FilterTextInput
            changeFilters={changeFilters}
            keyName="campaignReference"
            value={campaignReference}
            name="campaign-reference"
            placeholderText="Campaign ID"
          />
        </div>
        <div className={filterItemClassName}>
          <BaseSelect
            dataTestId="campaign-media-type-select"
            name="campaignMediaType"
            options={CAMPAIGN_MEDIA_TYPES}
            selectedValue={findOption(CAMPAIGN_MEDIA_TYPES, 'code', campaignMediaType)}
            onSelect={(_: string, value: CodeNameModel) => changeFilters({ campaignMediaType: value?.code })}
            placeholder="Media type"
            optionControl="radio"
            isClearable
          />
        </div>
        <div className={dateRangeClassName}>
          <FilterDateRange
            changeFilters={changeFilters}
            selectedStartDate={startDate}
            selectedEndDate={endDate}
            placeholderStartDate="From"
            placeholderEndDate="To"
          />
        </div>
      </div>
      <AdvancedFilters
        changeFilters={changeFilters}
        clearAllButton={
          <Button
            onClick={handleClearAllFilters}
            isDisabled={!hasActiveFilters}
            label="Clear all"
            svg={closeSvg}
            dataTestId="clear-all-filters-button"
            color={Color.TERTIARY}
            size={Size.LARGE}
            classNames="py-2.5 shrink-0"
          />
        }
        filters={{
          salesTeam,
          salesPerson,
          adminPerson,
          advertiser,
          brand,
          productCategory,
          agency,
          specialist,
          updatedBy,
          createdBy,
          createdAt,
          dropDate,
        }}
      />
    </FilterArea>
  );
};

export default Filters;
